// src/utils/dispositionAccueil.ts — SCE Issues
// Mon accueil, mes files (chantier du 03/11, docs/chantier-accueil.md) : où se
// rangent les cartes de l'accueil et dans quel ordre viennent les files.
//
// ─── Un modèle, des différences ───
//
// Chaque rôle a un MODÈLE (ici ceux du code ; ceux de /admin au lot 6). On
// n'enregistre pour chacun que ce qui en DIFFÈRE : l'ordre des cartes s'il l'a
// changé, les cartes masquées, celles ajoutées depuis le catalogue, et les
// cartes du modèle qu'il connaissait. Ainsi, quand le modèle change (carte
// ajoutée par l'administrateur ou par une version de l'appli), la nouveauté
// arrive chez tout le monde, à sa place, marquée « Nouveau » chez ceux qui
// avaient déjà rangé leur accueil — sauf s'ils l'avaient masquée.
//
// Règles :
//  - une carte disponible du modèle est TOUJOURS là, sauf masquée ;
//  - une carte imposée (cadenas) ne se masque pas, une carte non masquable non plus ;
//  - une carte qui n'est plus disponible (droits) disparaît, sans être « masquée » :
//    elle revient d'elle-même si le droit revient ;
//  - une carte retirée du modèle disparaît, sauf si la personne l'avait ajoutée.
//
// Pur : aucun réseau, aucun React. Les files (« À qualifier », « Critiques en
// cours »…) suivent les mêmes règles, sur une seule rangée.

import type { IdFile } from './accueil'
import { fileDeVue, lireFilesComposees, MAX_FILES, type FileComposee } from './filesComposees'
import type { VuePerso } from './tableauProblemes'

export const NB_COLONNES = 3

const estObjet = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v)

// ─── Catalogue ───

export type IdCarte =
  | 'file' | 'activite' | 'revueFnct' | 'actionsFnct' | 'suivis'
  // Accueil opérateur (lot 8)
  | 'declarer' | 'nouveautes' | 'mesDeclarations' | 'derniers'

export interface DefinitionCarte {
  titre: string
  /** Une phrase pour le catalogue. */
  description: string
  /** Faux : la carte ne se retire pas (la file choisie : les compteurs y mènent). */
  masquable: boolean
  /** Prend la hauteur restante de sa colonne et défile (file, activité). */
  grande: boolean
  /** Plafond de hauteur dans une colonne partagée (petites cartes). */
  hauteurMax?: string
}

export const CARTES_ACCUEIL: Record<IdCarte, DefinitionCarte> = {
  file: {
    titre: 'La file choisie',
    description: 'Les problèmes de la file cliquée dans les compteurs.',
    masquable: false,
    grande: true,
  },
  activite: {
    titre: 'Activité récente',
    description: 'Ce qui s’est passé sur vos métiers et ce que vous suivez.',
    masquable: true,
    grande: true,
  },
  revueFnct: {
    titre: 'Revue des FNC-T',
    description: 'Prêtes à clore, actions en retard, depuis la dernière revue.',
    masquable: true,
    grande: false,
    hauteurMax: '40%',
  },
  actionsFnct: {
    titre: 'Vos actions FNC-T',
    description: 'Vos deux actions les plus urgentes, à clore ou reporter sur place.',
    masquable: true,
    grande: false,
    hauteurMax: '35%',
  },
  suivis: {
    titre: 'Éléments suivis',
    description: 'Les problèmes, équipements et projets que vous suivez.',
    masquable: true,
    grande: false,
  },
  declarer: {
    titre: 'Déclarer',
    description: 'Le bouton pour déclarer un problème ou une NC-S.',
    masquable: false,
    grande: false,
  },
  nouveautes: {
    titre: 'Du nouveau sur vos déclarations',
    description: 'Ce que les autres ont fait sur vos déclarations et vos suivis depuis votre dernière visite.',
    masquable: true,
    grande: false,
    hauteurMax: '40%',
  },
  mesDeclarations: {
    titre: 'Mes déclarations',
    description: 'Tout ce que vous avez déclaré, le plus récent d’abord.',
    masquable: true,
    grande: true,
  },
  derniers: {
    titre: 'Derniers problèmes déclarés',
    description: 'Avant de déclarer : le vôtre y est peut-être déjà.',
    masquable: true,
    grande: true,
  },
}

export const IDS_CARTES = Object.keys(CARTES_ACCUEIL) as IdCarte[]

export const estIdCarte = (v: unknown): v is IdCarte => typeof v === 'string' && v in CARTES_ACCUEIL

// ─── Une file affichée en carte (lot 4) ───
// « Critiques en cours » en permanence dans une colonne, à côté de la file
// choisie : la carte `file:critiques`, ajoutée comme une autre.

const PREFIXE_CARTE_FILE = 'file:'

export const carteDeFile = (idFile: string): string => `${PREFIXE_CARTE_FILE}${idFile}`

/** L'identifiant de la file d'une carte `file:…`, sinon null. */
export function fileDeCarte(id: string): string | null {
  return id.startsWith(PREFIXE_CARTE_FILE) && id.length > PREFIXE_CARTE_FILE.length ? id.slice(PREFIXE_CARTE_FILE.length) : null
}

/** Une carte de file prend la hauteur qui reste, comme la file choisie. */
export function estGrande(id: string): boolean {
  return estIdCarte(id) ? CARTES_ACCUEIL[id].grande : fileDeCarte(id) !== null
}

// ─── Réglages d'une file (lot 4) ───

/** Délais proposés pour « Sans activité depuis … ». */
export const DELAIS_SANS_ACTIVITE = [3, 7, 14] as const

export interface ReglageFile {
  /** « Sans activité depuis » : 3, 7 ou 14 jours. */
  delai?: number
  /** Point rouge sur le compteur au-delà de N problèmes… */
  alerteNombre?: number
  /** … ou quand un problème attend depuis plus de N jours. */
  alerteJours?: number
}

const entierPositif = (v: unknown, max: number): number | undefined =>
  typeof v === 'number' && Number.isInteger(v) && v > 0 && v <= max ? v : undefined

function lireReglageFile(v: unknown): ReglageFile | null {
  if (!estObjet(v)) return null
  const r: ReglageFile = {}
  if (typeof v.delai === 'number' && (DELAIS_SANS_ACTIVITE as readonly number[]).includes(v.delai)) r.delai = v.delai
  const n = entierPositif(v.alerteNombre, 999)
  if (n) r.alerteNombre = n
  const j = entierPositif(v.alerteJours, 365)
  if (j) r.alerteJours = j
  return Object.keys(r).length ? r : null
}

function lireReglagesFiles(v: unknown): Record<string, ReglageFile> {
  if (!estObjet(v)) return {}
  const res: Record<string, ReglageFile> = {}
  for (const [id, brut] of Object.entries(v)) {
    const r = lireReglageFile(brut)
    if (r) res[id] = r
  }
  return res
}

/** Seuils retirés des réglages vides : un réglage effacé ne laisse pas de trace. */
export function nettoyerReglages(reglages: Readonly<Record<string, ReglageFile>>): Record<string, ReglageFile> {
  return lireReglagesFiles(JSON.parse(JSON.stringify(reglages)) as unknown)
}

/** La file dépasse-t-elle un de ses seuils ? (point rouge sur son compteur) */
export function enAlerte(nombre: number, attenteJours: number, r: ReglageFile | undefined): boolean {
  if (!r || nombre === 0) return false
  return (r.alerteNombre !== undefined && nombre > r.alerteNombre) || (r.alerteJours !== undefined && attenteJours > r.alerteJours)
}

/** « Plus de 5 problèmes · Attente de plus de 3 j » : le seuil en clair. */
export function libelleAlerte(r: ReglageFile | undefined): string {
  const morceaux: string[] = []
  if (r?.alerteNombre !== undefined) morceaux.push(`plus de ${r.alerteNombre} problème${r.alerteNombre > 1 ? 's' : ''}`)
  if (r?.alerteJours !== undefined) morceaux.push(`attente de plus de ${r.alerteJours} j`)
  const t = morceaux.join(' ou ')
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : ''
}

// ─── Modèles ───

export type RoleAccueil = 'coordinateur' | 'administrateur' | 'operateur'

export interface ModeleAccueil {
  /** Trois colonnes, de gauche à droite. */
  colonnes: string[][]
  /** Les files, dans l'ordre des compteurs. */
  files: string[]
  /** Cartes et files au cadenas : déplaçables, jamais retirées. */
  imposees: string[]
  /**
   * L'ordre des cartes au téléphone (lot 7), une seule colonne ; absent = les
   * colonnes du bureau l'une après l'autre. Celui du code est l'ordre d'avant
   * le chantier : les petites cartes FNC-T avant l'activité.
   */
  telephone?: string[]
}

/** Les modèles du code : l'accueil tel qu'il était avant le chantier. */
export const MODELES_ACCUEIL_DEFAUT: Record<RoleAccueil, ModeleAccueil> = {
  coordinateur: {
    colonnes: [['file'], ['activite'], ['revueFnct', 'actionsFnct', 'suivis']],
    files: ['aQualifier', 'critiques', 'sansActivite', 'enCours'] satisfies IdFile[],
    imposees: ['aQualifier'],
    telephone: ['file', 'revueFnct', 'actionsFnct', 'activite', 'suivis'],
  },
  administrateur: {
    colonnes: [['file'], ['activite'], ['revueFnct', 'actionsFnct', 'suivis']],
    files: ['aQualifier', 'critiques', 'sansActivite', 'enCours'] satisfies IdFile[],
    imposees: [],
    telephone: ['file', 'revueFnct', 'actionsFnct', 'activite', 'suivis'],
  },
  // Lot 8 : l'accueil d'avant (Lot 9) — déclarer et suivre ses déclarations,
  // les derniers problèmes, l'activité. Pas de compteurs. Au téléphone, ni
  // « Mes déclarations » ni l'activité (« bureau seulement », qu'on peut y faire venir).
  operateur: {
    colonnes: [['declarer', 'revueFnct', 'actionsFnct', 'nouveautes', 'suivis', 'mesDeclarations'], ['derniers'], ['activite']],
    files: [],
    imposees: [],
    telephone: ['declarer', 'revueFnct', 'actionsFnct', 'nouveautes', 'suivis', 'derniers'],
  },
}

// ─── Différences enregistrées ───

export interface DifferencesAccueil {
  version: 1
  /** L'ordre choisi, colonne par colonne ; null = celui du modèle. */
  colonnes: string[][] | null
  masquees: string[]
  ajoutees: string[]
  /** Cartes du modèle déjà vues : les autres arrivent « Nouveau ». */
  connues: string[]
  files: string[] | null
  filesMasquees: string[]
  filesConnues: string[]
  /** Délai et seuils d'alerte de chaque file : à la personne, pas au modèle. */
  reglagesFiles: Record<string, ReglageFile>
  /** Les files composées par la personne (lot 5), en bulles ou liées à une vue de /issues. */
  filesComposees: FileComposee[]
  /** Son téléphone (lot 7) : son ordre à elle, les cartes retirées du téléphone seulement. */
  telephone: ReglageTelephone
}

// ─── Le téléphone (lot 7) ───
// Une colonne : les cartes du bureau, dans l'ordre du modèle (ou celui de la
// personne). Une carte retirée du bureau n'y est pas ; une carte peut aussi être
// retirée du téléphone seul (sauf la file choisie et les imposées).

export interface ReglageTelephone {
  /** null : l'ordre du modèle. */
  ordre: string[] | null
  /** Retirées du téléphone. */
  caches: string[]
  /** Cartes « bureau seulement » du modèle que la personne fait venir au téléphone (lot 8). */
  montrees: string[]
}

export const TELEPHONE_DEFAUT: ReglageTelephone = { ordre: null, caches: [], montrees: [] }

function lireTelephone(v: unknown): ReglageTelephone {
  if (!estObjet(v)) return { ...TELEPHONE_DEFAUT }
  return { ordre: Array.isArray(v.ordre) ? textes(v.ordre) : null, caches: textes(v.caches), montrees: textes(v.montrees) }
}

/** Une carte du modèle absente de son ordre du téléphone : au bureau seulement, d'office. */
function bureauSeulement(id: string, modele: ModeleAccueil): boolean {
  return !!modele.telephone && !modele.telephone.includes(id) && modele.colonnes.some((c) => c.includes(id))
}

/** La carte est-elle affichée au téléphone ? */
export function visibleAuTelephone(id: string, modele: ModeleAccueil, tel: ReglageTelephone): boolean {
  if (!retirable(id, modele)) return true
  if (tel.caches.includes(id)) return false
  return !bureauSeulement(id, modele) || tel.montrees.includes(id)
}

/** Toutes les cartes du bureau, dans l'ordre du téléphone (retirées du téléphone comprises). */
export function ordreTelephone(colonnes: readonly (readonly string[])[], modele: ModeleAccueil, tel: ReglageTelephone): string[] {
  const presentes = colonnes.flat()
  const res = (tel.ordre ?? modele.telephone ?? presentes).filter((id, i, l) => presentes.includes(id) && l.indexOf(id) === i)
  // Une carte du bureau absente de l'ordre (ajoutée depuis) : à la suite, dans
  // l'ordre du bureau ; les cartes « bureau seulement » du modèle tout au bout.
  for (const id of presentes) if (!res.includes(id) && !bureauSeulement(id, modele)) res.push(id)
  for (const id of presentes) if (!res.includes(id)) res.push(id)
  return res
}

/** Les cartes affichées au téléphone. */
export function cartesAuTelephone(colonnes: readonly (readonly string[])[], modele: ModeleAccueil, tel: ReglageTelephone): string[] {
  return ordreTelephone(colonnes, modele, tel).filter((id) => visibleAuTelephone(id, modele, tel))
}

/** Monter (-1) ou descendre (+1) une carte au téléphone : l'ordre devient le sien. */
export function decalerTelephone(ordre: readonly string[], id: string, sens: -1 | 1): string[] {
  const i = ordre.indexOf(id)
  const j = i + sens
  if (i < 0 || j < 0 || j >= ordre.length) return [...ordre]
  const res = [...ordre]
  res.splice(i, 1)
  res.splice(j, 0, id)
  return res
}

/** Retirer une carte du téléphone, ou l'y mettre (jamais la file choisie, Déclarer ni une imposée). */
export function basculerTelephone(tel: ReglageTelephone, id: string, modele: ModeleAccueil): ReglageTelephone {
  const sans = (l: readonly string[]): string[] => l.filter((x) => x !== id)
  if (!retirable(id, modele)) return { ...tel, caches: sans(tel.caches) }
  if (visibleAuTelephone(id, modele, tel)) {
    return tel.montrees.includes(id) ? { ...tel, montrees: sans(tel.montrees) } : { ...tel, caches: [...tel.caches, id] }
  }
  return tel.caches.includes(id) ? { ...tel, caches: sans(tel.caches) } : { ...tel, montrees: [...tel.montrees, id] }
}

/** Ce qu'on affiche : les colonnes, les files, et ce qui vient d'arriver du modèle. */
export interface Disposition {
  colonnes: string[][]
  files: string[]
  reglagesFiles: Record<string, ReglageFile>
  filesComposees: FileComposee[]
  telephone: ReglageTelephone
  /** Cartes et files du modèle que la personne n'avait pas encore vues. */
  nouvelles: string[]
}

/** Ce que range le mode « Personnaliser » (sans les nouveautés ; le téléphone se range à part). */
export type Rangement = Pick<Disposition, 'colonnes' | 'files' | 'reglagesFiles' | 'filesComposees'> & { telephone?: ReglageTelephone }

function textes(v: unknown): string[] {
  if (!Array.isArray(v)) return []
  return [...new Set(v.filter((x): x is string => typeof x === 'string' && x.trim() !== '').map((x) => x.trim()))]
}

/** Lecture tolérante : un bloc absent ou abîmé = aucune différence (null). */
export function lireDifferences(brut: unknown): DifferencesAccueil | null {
  if (!estObjet(brut) || brut.version !== 1) return null
  const colonnes = Array.isArray(brut.colonnes) && brut.colonnes.length === NB_COLONNES ? brut.colonnes.map(textes) : null
  return {
    version: 1,
    colonnes,
    masquees: textes(brut.masquees),
    ajoutees: textes(brut.ajoutees),
    connues: textes(brut.connues),
    files: Array.isArray(brut.files) ? textes(brut.files) : null,
    filesMasquees: textes(brut.filesMasquees),
    filesConnues: textes(brut.filesConnues),
    reglagesFiles: lireReglagesFiles(brut.reglagesFiles),
    filesComposees: lireFilesComposees(brut.filesComposees),
    telephone: lireTelephone(brut.telephone),
  }
}

// ─── Fusion : modèle + différences = ce qu'on affiche ───

interface DiffListes {
  ordre: string[][] | null
  masquees: readonly string[]
  ajoutees: readonly string[]
  connues: readonly string[]
}

/**
 * Le cœur, commun aux cartes (trois listes) et aux files (une liste).
 * `diff` null : la personne n'a jamais rien rangé, c'est le modèle tel quel.
 */
function fusionnerListes(
  modele: readonly (readonly string[])[],
  diff: DiffListes | null,
  imposees: ReadonlySet<string>,
  disponible: (id: string) => boolean,
): { listes: string[][]; nouvelles: string[] } {
  const dansModele = new Set(modele.flat())
  const masquees = new Set((diff?.masquees ?? []).filter((id) => !imposees.has(id)))
  const ajoutees = new Set(diff?.ajoutees ?? [])
  const vus = new Set<string>()
  const garder = (id: string): boolean => {
    if (vus.has(id) || !disponible(id) || masquees.has(id)) return false
    if (!dansModele.has(id) && !ajoutees.has(id)) return false
    vus.add(id)
    return true
  }
  const base = diff?.ordre && diff.ordre.length === modele.length ? diff.ordre : modele
  const listes = base.map((l) => l.filter(garder))

  // Les cartes du modèle absentes de l'ordre enregistré (arrivées depuis, ou
  // revenues avec un droit) : à leur place dans le modèle.
  modele.forEach((l, j) => {
    l.forEach((id, k) => {
      if (vus.has(id) || !disponible(id) || masquees.has(id)) return
      vus.add(id)
      const cible = listes[j]!
      // Juste après la carte qui la précède dans le modèle, si elle est là.
      const avant = l.slice(0, k).reverse().find((x) => cible.includes(x))
      cible.splice(avant ? cible.indexOf(avant) + 1 : Math.min(k, cible.length), 0, id)
    })
  })

  const connues = new Set(diff?.connues ?? [])
  const nouvelles = diff ? [...dansModele].filter((id) => vus.has(id) && !connues.has(id)) : []
  return { listes, nouvelles }
}

/**
 * Ce que la personne voit : son modèle, ses différences, ses droits.
 * @param disponible une carte ou une file que la personne a le droit de voir
 */
export function disposer(
  modele: ModeleAccueil,
  diff: DifferencesAccueil | null,
  disponible: (id: string) => boolean = () => true,
): Disposition {
  const imposees = new Set(modele.imposees)
  // Les files composées sont à la personne : toujours disponibles, en compteur comme en carte.
  const composees = diff?.filesComposees ?? []
  const siennes = new Set(composees.map((f) => f.id))
  const dispo = (id: string): boolean => siennes.has(id) || siennes.has(fileDeCarte(id) ?? '') || disponible(id)
  const cartes = fusionnerListes(
    modele.colonnes,
    diff && { ordre: diff.colonnes, masquees: diff.masquees, ajoutees: diff.ajoutees, connues: diff.connues },
    imposees,
    dispo,
  )
  const files = fusionnerListes(
    [modele.files],
    diff && { ordre: diff.files ? [diff.files] : null, masquees: diff.filesMasquees, ajoutees: [...siennes], connues: diff.filesConnues },
    imposees,
    dispo,
  )
  return {
    colonnes: cartes.listes,
    files: files.listes[0] ?? [],
    reglagesFiles: diff?.reglagesFiles ?? {},
    filesComposees: composees,
    telephone: diff?.telephone ?? { ...TELEPHONE_DEFAUT },
    nouvelles: [...cartes.nouvelles, ...files.nouvelles],
  }
}

// ─── Différences : ce qu'on affiche − le modèle = ce qu'on enregistre ───

function differencesListes(
  listes: readonly (readonly string[])[],
  modele: readonly (readonly string[])[],
  disponible: (id: string) => boolean,
): DiffListes {
  const presentes = new Set(listes.flat())
  const dansModele = modele.flat()
  const masquees = dansModele.filter((id) => disponible(id) && !presentes.has(id))
  const ajoutees = [...presentes].filter((id) => !dansModele.includes(id))
  // L'ordre n'est gardé que s'il diffère de celui du modèle (cartes masquées
  // ou indisponibles mises à part) : sinon, la personne suit le modèle.
  const attendu = modele.map((l) => l.filter((id) => presentes.has(id)))
  const memeOrdre = ajoutees.length === 0 && JSON.stringify(attendu) === JSON.stringify(listes)
  return {
    ordre: memeOrdre ? null : listes.map((l) => [...l]),
    masquees,
    ajoutees,
    connues: dansModele.filter(disponible),
  }
}

/**
 * Ce qu'on enregistre pour une disposition rangée à la main. Rien à retenir
 * (identique au modèle) = null : le bloc est effacé, la personne suit le modèle.
 */
export function differences(
  disposition: Pick<Disposition, 'colonnes' | 'files'> & Partial<Pick<Disposition, 'reglagesFiles' | 'filesComposees' | 'telephone'>>,
  modele: ModeleAccueil,
  disponible: (id: string) => boolean = () => true,
): DifferencesAccueil | null {
  // Une file composée retirée des compteurs ET des colonnes est supprimée.
  const utilisees = new Set([...disposition.files, ...disposition.colonnes.flat().map((id) => fileDeCarte(id) ?? id)])
  const filesComposees = (disposition.filesComposees ?? []).filter((f) => utilisees.has(f.id))
  const c = differencesListes(disposition.colonnes, modele.colonnes, disponible)
  const f = differencesListes([disposition.files], [modele.files], disponible)
  const reglagesFiles = nettoyerReglages(disposition.reglagesFiles ?? {})
  // Le téléphone : seulement les cartes encore au bureau.
  const auBureau = new Set(disposition.colonnes.flat())
  const tel = disposition.telephone ?? TELEPHONE_DEFAUT
  const telephone: ReglageTelephone = {
    ordre: tel.ordre ? tel.ordre.filter((id) => auBureau.has(id)) : null,
    caches: tel.caches.filter((id) => auBureau.has(id)),
    montrees: (tel.montrees ?? []).filter((id) => auBureau.has(id)),
  }
  const vide =
    !c.ordre && !c.masquees.length && !c.ajoutees.length && !f.ordre && !f.masquees.length && !Object.keys(reglagesFiles).length && !filesComposees.length &&
    !telephone.ordre && !telephone.caches.length && !telephone.montrees.length
  if (vide) return null
  return {
    version: 1,
    colonnes: c.ordre,
    masquees: [...c.masquees],
    ajoutees: [...c.ajoutees],
    connues: [...c.connues],
    files: f.ordre ? f.ordre[0]! : null,
    filesMasquees: [...f.masquees],
    filesConnues: [...f.connues],
    reglagesFiles,
    filesComposees,
    telephone,
  }
}

/**
 * « Épingler à l'accueil » une vue personnelle de /issues (lot 5) : une file
 * liée à la vue, ajoutée au bout des compteurs. Rien si elle y est déjà, ou si
 * les compteurs sont pleins (`MAX_FILES`) : `null` avec la raison.
 */
export function epinglerVue(
  diff: DifferencesAccueil | null,
  modele: ModeleAccueil,
  vue: VuePerso,
): { diff: DifferencesAccueil | null; erreur: string | null } {
  const d = disposer(modele, diff)
  if (d.filesComposees.some((f) => f.vue === vue.id && d.files.includes(f.id))) return { diff, erreur: 'Cette vue est déjà une file de votre accueil.' }
  if (d.files.length >= MAX_FILES) return { diff, erreur: `Votre accueil a déjà ${MAX_FILES} files : retirez-en une avant.` }
  const file = fileDeVue(vue)
  const filesComposees = [...d.filesComposees.filter((f) => f.id !== file.id), file]
  return { diff: differences({ ...d, files: [...d.files, file.id], filesComposees }, modele), erreur: null }
}

/** Une vue de /issues est-elle épinglée en file ? */
export function vueEpinglee(diff: DifferencesAccueil | null, vueId: string): boolean {
  return !!diff?.filesComposees.some((f) => f.vue === vueId && (diff.files ?? []).includes(f.id))
}

// ─── Gestes du mode « Personnaliser » ───

function sans(colonnes: readonly (readonly string[])[], id: string): string[][] {
  return colonnes.map((l) => l.filter((x) => x !== id))
}

/** Déplace une carte à la colonne `col`, au rang `rang` (compté sans elle). */
export function deplacerCarte(colonnes: readonly (readonly string[])[], id: string, col: number, rang: number): string[][] {
  const res = sans(colonnes, id)
  const cible = res[Math.max(0, Math.min(col, res.length - 1))]!
  cible.splice(Math.max(0, Math.min(rang, cible.length)), 0, id)
  return res
}

/** Une carte d'un cran vers le haut (-1) ou le bas (+1) dans sa colonne. */
export function decalerCarte(colonnes: readonly (readonly string[])[], id: string, sens: -1 | 1): string[][] {
  const col = colonnes.findIndex((l) => l.includes(id))
  if (col < 0) return colonnes.map((l) => [...l])
  const rang = colonnes[col]!.indexOf(id) + sens
  if (rang < 0 || rang >= colonnes[col]!.length) return colonnes.map((l) => [...l])
  return deplacerCarte(colonnes, id, col, rang)
}

/** Peut-on retirer cette carte ou cette file ? Ni imposée, ni structurelle (une carte de file se retire). */
export function retirable(id: string, modele: ModeleAccueil): boolean {
  if (modele.imposees.includes(id)) return false
  return estIdCarte(id) ? CARTES_ACCUEIL[id].masquable : true
}

export function masquerCarte(colonnes: readonly (readonly string[])[], id: string, modele: ModeleAccueil): string[][] {
  return retirable(id, modele) ? sans(colonnes, id) : colonnes.map((l) => [...l])
}

/** Ajoute une carte du catalogue en bas de la colonne `col` (celle du modèle par défaut, sinon la dernière). */
export function ajouterCarte(colonnes: readonly (readonly string[])[], id: string, modele: ModeleAccueil, col?: number): string[][] {
  if (colonnes.some((l) => l.includes(id))) return colonnes.map((l) => [...l])
  const duModele = modele.colonnes.findIndex((l) => l.includes(id))
  const j = col ?? (duModele >= 0 ? duModele : colonnes.length - 1)
  return deplacerCarte(colonnes, id, j, colonnes[j]?.length ?? 0)
}

/**
 * Les cartes du catalogue qu'on peut encore ajouter.
 * @param files les files de la personne : chacune peut venir en carte (`file:…`)
 */
export function cartesAjoutables(
  colonnes: readonly (readonly string[])[],
  disponible: (id: string) => boolean = () => true,
  files: readonly string[] = [],
): string[] {
  const presentes = new Set(colonnes.flat())
  return [...IDS_CARTES, ...files.map(carteDeFile)].filter((id) => !presentes.has(id) && disponible(id))
}

// ─── Gestes sur les files (une rangée de compteurs) ───

/** Déplace une file au rang `rang` (compté sans elle). */
export function deplacerFile(files: readonly string[], id: string, rang: number): string[] {
  return deplacerCarte([files], id, 0, rang)[0]!
}

/** Une file retirée des compteurs (jamais une imposée, jamais la dernière). */
export function masquerFile(files: readonly string[], id: string, modele: ModeleAccueil): string[] {
  if (!retirable(id, modele) || files.length <= 1) return [...files]
  return files.filter((x) => x !== id)
}

/** Une file remise au bout des compteurs. */
export function ajouterFile(files: readonly string[], id: string): string[] {
  return files.includes(id) ? [...files] : [...files, id]
}

/** Le réglage d'une file changé (`undefined` efface ce point du réglage). */
export function reglerFile(reglages: Readonly<Record<string, ReglageFile>>, id: string, changement: Partial<ReglageFile>): Record<string, ReglageFile> {
  return nettoyerReglages({ ...reglages, [id]: { ...reglages[id], ...changement } })
}

// ─── Modèles de /admin › « Accueil par rôle » (lot 6) ───
// Le modèle réglé par l'administrateur est rangé comme les DIFFÉRENCES d'une
// personne, mais par rapport au modèle du code : une carte qu'une nouvelle
// version de l'appli ajoute au code arrive donc aussi dans un modèle déjà
// réglé, à sa place. Les cartes et files au cadenas sont écrites en entier.

export interface ModeleEnregistre {
  diff: DifferencesAccueil | null
  imposees: string[]
  /** Qui l'a réglé, et quand (ISO). */
  par: string
  le: string
}

export type ModelesEnregistres = Partial<Record<RoleAccueil, ModeleEnregistre>>

export const ROLES_ACCUEIL: readonly RoleAccueil[] = ['coordinateur', 'administrateur', 'operateur']

/** Lecture tolérante du bloc `modelesAccueil` de la ligne `__global__`. */
export function lireModelesAccueil(brut: unknown): ModelesEnregistres {
  if (!estObjet(brut) || brut.version !== 1 || !estObjet(brut.modeles)) return {}
  const res: ModelesEnregistres = {}
  for (const role of ROLES_ACCUEIL) {
    const m = brut.modeles[role]
    if (!estObjet(m)) continue
    res[role] = {
      diff: lireDifferences(m.diff),
      imposees: textes(m.imposees),
      par: typeof m.par === 'string' ? m.par : '',
      le: typeof m.le === 'string' ? m.le : '',
    }
  }
  return res
}

/** Le modèle qui s'applique : celui du code, ou celui de /admin posé dessus. */
export function modeleEffectif(code: ModeleAccueil, enregistre: ModeleEnregistre | undefined): ModeleAccueil {
  if (!enregistre) return code
  const d = disposer(code, enregistre.diff)
  const presentes = new Set([...d.colonnes.flat(), ...d.files])
  return { colonnes: d.colonnes, files: d.files, imposees: enregistre.imposees.filter((id) => presentes.has(id)), telephone: code.telephone }
}

/** Ce qu'on range pour un modèle réglé dans /admin ; null : identique au code (le bloc du rôle est retiré). */
export function modeleAEnregistrer(
  rangement: Pick<Disposition, 'colonnes' | 'files'>,
  imposees: readonly string[],
  code: ModeleAccueil,
  par: string,
  le: string,
): ModeleEnregistre | null {
  const diff = differences({ colonnes: rangement.colonnes, files: rangement.files }, code)
  const presentes = new Set([...rangement.colonnes.flat(), ...rangement.files])
  const garder = [...new Set(imposees)].filter((id) => presentes.has(id))
  const memesImposees = JSON.stringify([...garder].sort()) === JSON.stringify([...code.imposees].sort())
  if (!diff && memesImposees) return null
  return { diff, imposees: garder, par, le }
}

/** Imposer une carte ou une file, ou la libérer (la file choisie n'est jamais à imposer : elle est toujours là). */
export function basculerImposee(imposees: readonly string[], id: string): string[] {
  if (estIdCarte(id) && !CARTES_ACCUEIL[id].masquable) return [...imposees]
  return imposees.includes(id) ? imposees.filter((x) => x !== id) : [...imposees, id]
}
